import { forwardRef, useContext, useRef } from 'react'
import { useComposedRefs } from '../../../../../utils/customHooks'
import { RadioGroupContext } from './radio-context'
import RadioRoot from './radio-root'
import {
  StyledLabel,
  StyledSecondaryLabel,
  StyledLabelSkeleton,
  StyledRadioContainer,
  StyledRadioIndicatorSkeleton,
  StyledRadioItemSkeleton,
  LabelContainer
} from '../styled-components'
import PropTypes from 'prop-types'

const RadioItem = forwardRef((props, forwardedRef) => {
  const {
    label,
    labelCustomStyle,
    secondaryLabel,
    secondaryLabelCustomStyle,
    labelContainerCustomStyle,
    disabled,
    customOnCheck,
    value,
    ...itemProps
  } = props
  const ref = useRef(null)
  const composedRefs = useComposedRefs(forwardedRef, ref)

  const groupContext = useContext(RadioGroupContext)

  const isDisabled = groupContext?.disabled || disabled
  const checked = groupContext?.value === value

  if (groupContext?.loading) {
    return (
      <StyledRadioContainer>
        <StyledRadioItemSkeleton size={groupContext?.size}>
          {checked && (
            <StyledRadioIndicatorSkeleton size={groupContext?.size} />
          )}
        </StyledRadioItemSkeleton>
        {label && (
          <StyledLabelSkeleton
            size={groupContext?.size}
            disabled={groupContext?.disabled}
          >
            {label}
          </StyledLabelSkeleton>
        )}
        {secondaryLabel && (
          <StyledLabelSkeleton
            size={groupContext?.size}
            disabled={groupContext?.disabled}
          >
            {secondaryLabel}
          </StyledLabelSkeleton>
        )}
      </StyledRadioContainer>
    )
  }

  return (
    <StyledRadioContainer>
      <RadioRoot
        variant={groupContext?.variant}
        size={groupContext?.size}
        disabled={isDisabled}
        required={groupContext?.required}
        checked={checked}
        value={value}
        {...itemProps}
        name={groupContext?.name}
        ref={composedRefs}
        onCheck={() => {
          groupContext?.onValueChange(value)
          customOnCheck && customOnCheck()
        }}
        onKeyDown={(event) => {
          // According to WAI ARIA, radio groups don't activate items on enter keypress
          if (event.key === 'Enter') event.preventDefault()
        }}
      />
      <LabelContainer style={labelContainerCustomStyle}>
        {label && (
          <StyledLabel
            size={groupContext?.size}
            data-disabled={groupContext?.disabled ? '' : undefined}
            disabled={groupContext?.disabled}
            style={labelCustomStyle}
          >
            {label}
          </StyledLabel>
        )}
        {secondaryLabel && (
          <StyledSecondaryLabel
            size={groupContext?.size}
            data-disabled={groupContext?.disabled ? '' : undefined}
            disabled={groupContext?.disabled}
            style={secondaryLabelCustomStyle}
          >
            {secondaryLabel}
          </StyledSecondaryLabel>
        )}
      </LabelContainer>
    </StyledRadioContainer>
  )
})

RadioItem.propTypes = {
  value: PropTypes.string,
  customOnCheck: PropTypes.func,
  label: PropTypes.string,
  labelCustomStyle: PropTypes.object,
  secondaryLabel: PropTypes.string,
  secondaryLabelCustomStyle: PropTypes.object,
  labelContainerCustomStyle: PropTypes.object
}

RadioItem.displayName = 'RadioItem'
export default RadioItem
